// src/helpers/token.js
// Robust helpers for reading tokens from localStorage in various historical formats

import config from "config";

/**
 * Reads the access token from localStorage for the current client ID.
 * Handles historical formats:
 * - Raw JWT string stored directly
 * - JSON-stringified string (e.g., JSON.stringify(tokenString))
 * - JSON-object with an accessToken property (e.g., legacy user.result)
 */
export const getAccessTokenFromStorage = () => {
  const key = `ads.auth.token.key${config.adsMsalConfig.clientID}`;
  const raw = localStorage.getItem(key);
  if (!raw) return null;

  let parsed = raw;
  try {
    parsed = JSON.parse(raw);
  } catch (_e) {
    // not JSON, keep as raw string
  }

  if (typeof parsed === "string") {
    return parsed;
  }

  if (parsed && typeof parsed === "object") {
    const maybeObj = parsed;
    const accessToken = maybeObj["accessToken"];
    if (typeof accessToken === "string") return accessToken;
    const token = maybeObj["token"];
    if (typeof token === "string") return token;
  }

  return null;
};

/** Renew proactively when the bearer expires within this window (DE-9510). */
export const TOKEN_RENEW_BEFORE_EXPIRY_MS = 5 * 60 * 1000;

/**
 * Milliseconds until JWT exp, or null when exp is missing/unparseable.
 */
export const getTokenExpiresInMs = (token, nowMs = Date.now()) => {
  const payload = tryParseJwt(token);
  if (!payload || !payload.exp) return null;
  return payload.exp * 1000 - nowMs;
};

/**
 * True when the token will expire within `thresholdMs` (default 5 minutes).
 */
export const isTokenExpiringSoon = (
  token,
  thresholdMs = TOKEN_RENEW_BEFORE_EXPIRY_MS,
  nowMs = Date.now(),
) => {
  const expiresInMs = getTokenExpiresInMs(token, nowMs);
  if (expiresInMs === null) return false;
  return expiresInMs <= thresholdMs;
};

/**
 * Utility to decode JWT payload safely. Returns null on failure.
 */
export const tryParseJwt = (token) => {
  if (!token) return null;
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    // Convert base64url to base64
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    // Decode and handle UTF-8 properly
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(json);
  } catch (_e) {
    return null;
  }
};
